import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/Tree/avlIntro.css"
import { Col } from "reactstrap"
import "../../../assets/css/GameField.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const correctAVLStructure = [
    { value: 50, balance: 0 },      // index 0 - root
    { value: 30, balance: 0 },      // index 1
    { value: 70, balance: 0 },      // index 2
    { value: 20, balance: 0 },      // index 3
    { value: 40, balance: 0 },      // index 4
    { value: 60, balance: 0 },      // index 5
    { value: 80, balance: 0 },      // index 6
    { value: 10, balance: 0 },      // index 7
    { value: 25, balance: 0 },      // index 8
    null,                            // index 9
    null,                            // index 10
    null,                            // index 11
    null,                            // index 12
    { value: 75, balance: 0 },      // index 13
    { value: 90, balance: 0 }       // index 14
];
const AVLTreeBuilder = () => {

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }

    const [treeNodes, setTreeNodes] = useState(Array(15).fill(null));
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [showBalanceFactors, setShowBalanceFactors] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const handlePlayAgain = () => {
        initializeGame();
    };

    const mission = selLanguage ? "Κατασκεύασε σωστά ένα AVL Δυαδικό Δέντρο Αναζήτησης (AVL Tree) σέρνοντας τους κόμβους στις κατάλληλες θέσεις. Θυμήσου: AVL = BST + Ισορροπία!" : "Construct an AVL Binary Search Tree (AVL Tree) by dragging the nodes into the appropriate positions. Remember: AVL = BST + Balance!"

    // Mock game data - replace with actual localStorage logic if needed

    useEffect(() => {
        initializeGame();
    }, []);

    const initializeGame = () => {
        // Δημιούργησε available nodes χωρίς να χρησιμοποιήσεις correctAVLStructure
        const nodesToPlace = [30, 70, 20, 40, 60, 80, 10, 25, 75, 90];
        const shuffled = [...nodesToPlace].sort(() => 0.5 - Math.random());

        // Decoy nodes
        const decoyNodes = [5, 15, 35, 95, 100];
        const mixed = [...shuffled, ...decoyNodes].sort(() => 0.5 - Math.random());

        // Προ-τοποθέτηση ρίζας
        const initialTree = Array(15).fill(null);
        initialTree[0] = { value: 50, balance: 0 };

        setTreeNodes(initialTree);
        setAvailableNodes(mixed);
        setMessage("");
        setGameState("playing");
        setLives(3);
        setShowBalanceFactors(false);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const handleDragStart = (e, node) => {
        setDraggedNode(node);
        e.dataTransfer.setData("text/plain", node.toString());
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropOnNode = (index) => (e) => {
        e.preventDefault();
        const nodeValue = parseInt(e.dataTransfer.getData("text/plain"));

        if (!nodeValue || treeNodes[index]) return;

        // Create node object with placeholder balance factor
        const newNode = { value: nodeValue, balance: 0 };

        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = newNode;
            return updated;
        });

        setAvailableNodes((prev) => prev.filter((n) => n !== nodeValue));
    };

    const handleRemoveNode = (index) => {
        const node = treeNodes[index];
        if (!node || index === 0) return; // Can't remove root

        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[index] = null;
            return updated;
        });

        setAvailableNodes((prev) => [...prev, node.value]);
    };

    const calculateBalanceFactor = (index) => {
        if (!treeNodes[index]) return 0;

        const leftChild = index * 2 + 1;
        const rightChild = index * 2 + 2;

        const leftHeight = getSubtreeHeight(leftChild);
        const rightHeight = getSubtreeHeight(rightChild);

        return rightHeight - leftHeight;
    };

    const getSubtreeHeight = (index) => {
        if (index >= 15 || !treeNodes[index]) return 0;  // Αλλαγή από 7 σε 15

        const leftHeight = getSubtreeHeight(index * 2 + 1);
        const rightHeight = getSubtreeHeight(index * 2 + 2);

        return 1 + Math.max(leftHeight, rightHeight);
    };

    const isBSTValid = () => {
        return checkBSTProperty(0, -Infinity, Infinity);
    };

    const checkBSTProperty = (index, min, max) => {
        if (index >= 15 || !treeNodes[index]) return true;  // Αλλαγή από 7 σε 15

        const value = treeNodes[index].value;
        if (value <= min || value >= max) return false;

        return checkBSTProperty(index * 2 + 1, min, value) &&
            checkBSTProperty(index * 2 + 2, value, max);
    };

    const isAVLValid = () => {
        for (let i = 0; i < 15; i++) {  // Αλλαγή από 7 σε 15
            if (treeNodes[i]) {
                const bf = calculateBalanceFactor(i);
                if (Math.abs(bf) > 1) return false;
            }
        }
        return true;
    };

    const checkSolution = () => {
        const hasEnoughNodes = treeNodes.filter(node => node !== null).length >= 9; // τουλάχιστον 9 κόμβοι

        if (!hasEnoughNodes) {

            const newLives = lives - 1;
            setLives(newLives);
            if (newLives <= 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? "Έχασες! Το δέντρο δεν τηρεί την ιδιότητα BST!" : "Game Over! Tree doesn't satisfy BST property!");
            } else {
                setMessage(selLanguage ? "Τοποθέτησε περισσότερους κόμβους!" : "Place more nodes!");
            }
            return;
        }

        if (!isBSTValid()) {
            const newLives = lives - 1;
            setLives(newLives);

            if (newLives <= 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? "Έχασες! Το δέντρο δεν τηρεί την ιδιότητα BST!" : "Game Over! Tree doesn't satisfy BST property!");
            } else {
                setMessage(selLanguage ? "❌ Το δέντρο δεν τηρεί την ιδιότητα BST! Αριστερό < Γονέας < Δεξί" : "❌ The tree doesn't satisfy BST property! Left < Parent < Right");
            }
            return;
        }

        if (!isAVLValid()) {
            const newLives = lives - 1;
            setLives(newLives);

            if (newLives <= 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? "Έχασες! Κάποιοι συντελεστές ισορροπίας είναι εκτός [-1, +1]!" : "Game Over! Some balance factors are out of [-1, +1]!");
            } else {
                setMessage(selLanguage ? "❌ Κάποιοι συντελεστές ισορροπίας είναι εκτός [-1, +1]! Ελέγξτε την ισορροπία." : "❌ Some balance factors are out of [-1, +1]! Check the balance.");
            }
            return;
        }

        // ✅ ΕΠΙΤΥΧΙΑ - Δέχεται οποιαδήποτε σωστή AVL λύση!
        setGameState("won");
        setShowGameWon(true);
        setMessage(selLanguage ? "🎉 Μπράβο! Δημιούργησες σωστά ένα AVL δέντρο!" : "🎉 Well done! You've correctly constructed an AVL tree!");
    };

    const renderNode = (index) => {
        const node = treeNodes[index];
        const balanceFactor = node ? calculateBalanceFactor(index) : 0;
        const isBalanced = Math.abs(balanceFactor) <= 1;

        return (
            <div
                key={index}
                onDragOver={handleDragOver}
                onDrop={handleDropOnNode(index)}
                onClick={() => handleRemoveNode(index)}
                style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: node
                        ? (isBalanced ? "#3b82f6" : "#3b82f6")
                        : "#e5e7eb",
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    cursor: node && index !== 0 ? "pointer" : "default",
                    border: node
                        ? "#cbcfceff"
                        : "2px dashed #9ca3af",
                    position: "relative",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                }}
                title={node && index !== 0 ? selLanguage ? "Κλικ για αφαίρεση" : "Click to remove" : ""}
            >
                {node ? (
                    <>
                        <span>{node.value}</span>
                        {showBalanceFactors && (
                            <span style={{
                                fontSize: "10px",
                                color: isBalanced ? "#dcfce7" : "#fecaca",
                                fontWeight: "normal"
                            }}>
                                BF:{balanceFactor}
                            </span>
                        )}
                    </>
                ) : ""}
            </div>
        );
    };

    const formatExampleText = (text) => {
        return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    };

    const getNodePositions = (level, nodeIndex, totalLevels) => {
        const baseSpacing = 600;
        const levelSpacing = baseSpacing / Math.pow(2, level);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };

    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const renderEdges = () => {
        const edges = [];

        for (let i = 0; i < treeNodes.length; i++) {
            if (!treeNodes[i]) continue;

            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i, 5);

            if (leftChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke={treeNodes[leftChildIndex] ? "#64748b" : "#94a3b8"}
                        strokeWidth="2"
                        opacity={treeNodes[leftChildIndex] ? "0.8" : "0.35"}
                        strokeDasharray={treeNodes[leftChildIndex] ? "0" : "5,5"}
                    />
                );
            }

            if (rightChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke={treeNodes[rightChildIndex] ? "#64748b" : "#94a3b8"}
                        strokeWidth="2"
                        opacity={treeNodes[rightChildIndex] ? "0.8" : "0.35"}
                        strokeDasharray={treeNodes[rightChildIndex] ? "0" : "5,5"}
                    />
                );
            }
        }

        return edges;
    };

    const renderSvgNode = (nodeObj, index) => {
        const isRoot = index === 0;
        const level = getNodeLevel(index);
        const pos = getNodePositions(level, index, 5);
        const isEmpty = !nodeObj;

        const balanceFactor = nodeObj ? calculateBalanceFactor(index) : 0;
        const isBalanced = Math.abs(balanceFactor) <= 1;

        return (
            <g key={index}>
                <foreignObject
                    x={pos.x - 30}
                    y={pos.y}
                    width="60"
                    height="60"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnNode(index)(e)}
                >
                    <div
                        draggable={false}
                        onClick={() => !isEmpty && !isRoot ? handleRemoveNode(index) : null}
                        style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            backgroundColor: isEmpty ? "#e5e7eb" : (isBalanced ? "#3b82f6" : "#3b82f6"),
                            color: "white",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "14px",
                            cursor: !isEmpty && !isRoot ? "pointer" : "default",
                            border: isEmpty ? "2px dashed #9ca3af" : "2px solid #cbcfceff",
                            position: "relative",
                            transition: "all 0.3s ease",
                            filter: !isEmpty ? "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.2))" : "none"
                        }}
                        title={
                            nodeObj
                                ? (index !== 0 ? (selLanguage ? "Κλικ για αφαίρεση" : "Click to remove") : "")
                                : (selLanguage ? "Σύρε εδώ" : "Drop here")
                        }
                    >
                        {!isEmpty ? (
                            <>
                                <span>{nodeObj.value}</span>
                                {showBalanceFactors && (
                                    <span style={{
                                        fontSize: "10px",
                                        color: isBalanced ? "#dcfce7" : "#fecaca",
                                        fontWeight: "normal"
                                    }}>
                                        BF:{balanceFactor}
                                    </span>
                                )}
                            </>
                        ) : null}
                    </div>
                </foreignObject>
            </g>
        );
    };

    const renderTree = () => {
        const tree = treeNodes;
        const maxLevel = Math.floor(Math.log2(tree.length));
        const svgWidth = 800;
        const svgHeight = (maxLevel + 2) * 100 + 60;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "30px"
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "600px" }}
                >
                    {renderEdges()}
                    {tree.map((node, idx) => renderSvgNode(node, idx))}
                </svg>
            </div>
        );
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 3500);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div
            className='main-content avlIntroContainer'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}

            />
            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>

                    {/* Lives Section */}
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "5px" }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            justifyContent: "right",
                            gap: "12px"
                        }}>


                            <div style={{
                                display: "flex",
                                gap: "6px",
                                alignItems: "center"
                            }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                            transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                            transition: "all 0.3s ease",
                                            animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>


                        </div>

                        <style>{`
                        @keyframes heartbeat {
            0 %, 100 % { transform: scale(1); }
            50 % { transform: scale(1.1); }
        }
        `}</style>
                    </div>

                    <br />

                    {/* Instructions */}
                    <div className="avlIntroPron">
                        {mission}
                    </div>

                    {renderTree()}

                    {/* Available Nodes */}
                    <div className="avlIntroAvailableNodes">
                        <h3 style={{
                            fontWeight: "600",
                            marginBottom: "16px",
                            color: "white",
                            textAlign: "center"
                        }}>
                            {selLanguage ? "Διαθέσιμοι Κόμβοι:" : "Available Nodes:"}
                        </h3>
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            flexWrap: "wrap",
                            justifyContent: "center"
                        }}>
                            {availableNodes.map((nodeValue, idx) => (
                                <motion.div
                                    className="avlIntroNodes"
                                    key={`${nodeValue} -${idx} `}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, nodeValue)}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    title={selLanguage ? `${nodeValue} - Σύρε στο δέντρο` : `${nodeValue} - Drag to tree`}
                                >
                                    {nodeValue}
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Messages */}
                    <AnimatePresence>
                        {message && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                style={{
                                    textAlign: "center",
                                    padding: "20px",
                                    borderRadius: "16px",
                                    marginBottom: "20px",
                                    background: gameState === "won"
                                        ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                        : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                    border: `2px solid ${gameState === "won" ? "#10b981" : "#ef4444"} `,
                                    color: gameState === "won" ? "#059669" : "#dc2626",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    boxShadow: gameState === "won"
                                        ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                        : "0 10px 25px rgba(239, 68, 68, 0.2)",
                                    maxWidth: "700px",
                                    margin: "0 auto 20px auto",
                                    width: "100%"
                                }}
                            >
                                {message}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Buttons */}
                    <div className="avlIntroButtonWrapper">
                        <motion.button
                            className="commonGameCheckButton"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={checkSolution}
                        >
                            {selLanguage ? "Έλεγχος AVL" : "Check AVL"}
                        </motion.button>



                        <motion.button
                            className="commonGameDeleteButton"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={initializeGame}
                        >
                            {selLanguage ? "Καθαρισμός" : "Reset"}
                        </motion.button>
                    </div>
                </div>
            </Col>
        </div>
    );
};

export default AVLTreeBuilder;